import type * as PlatformAdminTypes from '@aaimplatform/socket-cloud-types/generated/platform-admin.types';
import type * as ComplianceServiceTypes from '@aaimplatform/socket-cloud-types/generated/compliance-service.types';
import type * as AuthenticationServiceTypes from '@aaimplatform/socket-cloud-types/generated/authentication-service.types';
import type * as SocketCloudUserManagementTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-user-management.types';
import type * as SocketCloudTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud.types';
import type * as SocketCloudAuthTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-authentication-authorization.types';
import type * as SocketCloudOrganizationTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-organization.types';
import type * as InstitutionalPlatformTypes from '@aaimplatform/socket-cloud-types/generated/institutional-platform.types';
import type { DatabaseTypes } from '@aaimplatform/socket-cloud-types';

/** A socket-cloud tenant: the Tenant schema of socket-cloud-organization.openapi.yaml. */
export type Tenant = SocketCloudOrganizationTypes.components['schemas']['Tenant'];

/**
 * The tenant the auth context exposes. The session carries only a tenant's id
 * and name (`user.tenants[]`), so the context holds those two fields rather
 * than a full Tenant it would have to invent.
 */
export type TenantSummary = Pick<Tenant, 'id' | 'name'>;

/** GET /api/v1/tenants/{tenantId}: the TenantDetail schema of socket-cloud-organization.openapi.yaml. */
export type TenantDetail = SocketCloudOrganizationTypes.components['schemas']['TenantDetail'];

export type TenantPlan = SocketCloudOrganizationTypes.components['schemas']['TenantPlan'];

export interface TenantSettings {
  allowUserRegistration: boolean;
  requireEmailVerification: boolean;
  passwordPolicy: PasswordPolicy;
  sessionTimeout: number; // in minutes
  mfaRequired: boolean;
  allowedAuthProviders: string[];
  customDomainEnabled: boolean;
  auditLogging: boolean;
  dataRetentionDays: number;
}

export interface TenantLimits {
  currentUsers: number;
  currentNodes: number;
  currentStorage: number; // in GB
  currentAPIRequests: number;
  lastResetAt: Date;
}

export interface TenantBilling {
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: 'active' | 'canceled' | 'past_due' | 'unpaid';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  trialEndsAt?: Date;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number; // days
  preventReuse: number; // last N passwords
}

/** User type for console app -- permissive to accommodate mock data */
export type User = DatabaseTypes.components['schemas']['User'];

export type UserRole = InstitutionalPlatformTypes.components['schemas']['UserRole'];

/** TenantRole -- permissive for console mock data */
export type TenantRole = SocketCloudOrganizationTypes.components['schemas']['TenantRole'];

export type Permission = SocketCloudAuthTypes.components['schemas']['Permission'];

export type UserPreferences = AuthenticationServiceTypes.components['schemas']['UserPreferences'];

export type AuthSession = SocketCloudTypes.components['schemas']['AuthSession'];

export type AuthProvider = SocketCloudUserManagementTypes.components['schemas']['AuthProvider'];

export interface AuthProviderConfig {
  clientId?: string;
  clientSecret?: string;
  scope?: string[];
  authUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  issuer?: string;
  // SAML specific
  ssoUrl?: string;
  sloUrl?: string;
  certificate?: string;
  // LDAP specific
  serverUrl?: string;
  baseDN?: string;
  bindDN?: string;
  bindPassword?: string;
}

/** RegisterRequest -- permissive for console mock data */
export type RegisterRequest = AuthenticationServiceTypes.components['schemas']['RegisterRequest'];

export type PasswordResetRequest = AuthenticationServiceTypes.components['schemas']['PasswordResetRequest'];

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

export interface MfaSetupRequest {
  userId: string;
  secret: string;
  code: string;
}

export interface AuthContext {
  user: User | null;
  tenant: TenantSummary | null;
  currentTenant: TenantSummary | null;
  currentUser: User | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Authentication
  logout: () => Promise<void>;
  register: (data: RegisterRequest) => Promise<User>;
  setTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  // User management
  updateProfile: (data: Partial<User>) => Promise<User>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<UserPreferences>;
  // Permissions and roles
  checkPermission: (resource: string, action: string) => boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
  isTenantOwner: () => boolean;
  isTenantAdmin: () => boolean;
  // Tenant management (for tenant admins/owners)
  updateTenant: (data: Partial<TenantSummary>) => Promise<TenantSummary>;
  updateTenantSettings: (settings: Partial<TenantSettings>) => Promise<TenantSettings>;
  // User management within tenant
  inviteUser: (email: string, roleId: string) => Promise<void>;
  getTenantUsers: () => Promise<User[]>;
  getUsersForTenant: (tenantId: string) => Promise<User[]>;
  updateTenantUser: (userId: string, data: Partial<User>) => Promise<User>;
  deactivateTenantUser: (userId: string) => Promise<void>;
  // Tenant role management
  createTenantRole: (role: Omit<TenantRole, 'id' | 'createdAt' | 'updatedAt'>) => Promise<TenantRole>;
  updateTenantRole: (roleId: string, data: Partial<TenantRole>) => Promise<TenantRole>;
  deleteTenantRole: (roleId: string) => Promise<void>;
  getTenantRoles: () => Promise<TenantRole[]>;
}

export interface AuthState {
  user: User | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthAction {
  type: 'LOGIN_START' | 'LOGIN_SUCCESS' | 'LOGIN_FAILURE' | 
        'LOGOUT' | 'REFRESH_TOKEN' | 'UPDATE_PROFILE' | 
        'UPDATE_PREFERENCES' | 'SET_LOADING' | 'CLEAR_ERROR';
  payload?: any;
}

// Pre-defined system roles
export const SYSTEM_ROLES = {
  PLATFORM_ADMIN: 'platform_admin', // Platform super admin
  SUPPORT: 'support', // Platform support
} as const;

// Pre-defined tenant roles
export const TENANT_ROLES = {
  OWNER: 'owner', // Tenant owner (full access)
  ADMIN: 'admin', // Tenant admin
  OPERATOR: 'operator', // Operations user
  VIEWER: 'viewer', // Read-only user
  MEMBER: 'member', // Basic member
} as const;

// Pre-defined permissions
export const PERMISSIONS = {
  // Nodes
  NODES_READ: 'nodes:read',
  NODES_WRITE: 'nodes:write',
  NODES_DELETE: 'nodes:delete',
  
  // Consensus
  CONSENSUS_READ: 'consensus:read',
  CONSENSUS_WRITE: 'consensus:write',
  
  // Network
  NETWORK_READ: 'network:read',
  NETWORK_WRITE: 'network:write',
  
  // CRDT
  CRDT_READ: 'crdt:read',
  CRDT_WRITE: 'crdt:write',
  
  // Security
  SECURITY_READ: 'security:read',
  SECURITY_WRITE: 'security:write',
  SECURITY_ADMIN: 'security:admin',
  
  // Monitoring
  MONITORING_READ: 'monitoring:read',
  MONITORING_WRITE: 'monitoring:write',
  
  // Storage
  STORAGE_READ: 'storage:read',
  STORAGE_WRITE: 'storage:write',
  STORAGE_ADMIN: 'storage:admin',
  
  // Alerts
  ALERTS_READ: 'alerts:read',
  ALERTS_WRITE: 'alerts:write',
  ALERTS_MANAGE: 'alerts:manage',
  
  // Configuration
  CONFIG_READ: 'config:read',
  CONFIG_WRITE: 'config:write',
  CONFIG_ADMIN: 'config:admin',
  
  // AI/MCP
  AI_READ: 'ai:read',
  AI_WRITE: 'ai:write',
  AI_ADMIN: 'ai:admin',
  
  // Tenant users and roles (within tenant)
  TENANT_USERS_READ: 'tenant:users:read',
  TENANT_USERS_WRITE: 'tenant:users:write',
  TENANT_USERS_INVITE: 'tenant:users:invite',
  TENANT_USERS_DELETE: 'tenant:users:delete',
  TENANT_ROLES_READ: 'tenant:roles:read',
  TENANT_ROLES_WRITE: 'tenant:roles:write',
  TENANT_ROLES_DELETE: 'tenant:roles:delete',
  
  // Tenant management
  TENANT_READ: 'tenant:read',
  TENANT_WRITE: 'tenant:write',
  TENANT_ADMIN: 'tenant:admin',
  TENANT_BILLING: 'tenant:billing',
  TENANT_SETTINGS: 'tenant:settings',
  
  // System-level (platform admin only)
  SYSTEM_TENANTS_READ: 'system:tenants:read',
  SYSTEM_TENANTS_WRITE: 'system:tenants:write',
  SYSTEM_TENANTS_DELETE: 'system:tenants:delete',
  SYSTEM_USERS_READ: 'system:users:read',
  SYSTEM_USERS_WRITE: 'system:users:write',
  SYSTEM_ROLES_READ: 'system:roles:read',
  SYSTEM_ROLES_WRITE: 'system:roles:write',
  SYSTEM_CONFIG_READ: 'system:config:read',
  SYSTEM_CONFIG_WRITE: 'system:config:write',
  SYSTEM_ADMIN: 'system:admin',
} as const;

// Default system role permissions (platform level)
export const DEFAULT_SYSTEM_ROLE_PERMISSIONS = {
  [SYSTEM_ROLES.PLATFORM_ADMIN]: Object.values(PERMISSIONS),
  [SYSTEM_ROLES.SUPPORT]: [
    PERMISSIONS.SYSTEM_TENANTS_READ,
    PERMISSIONS.SYSTEM_USERS_READ,
    PERMISSIONS.SYSTEM_ROLES_READ,
    PERMISSIONS.SYSTEM_CONFIG_READ,
  ],
} as const;

// Default tenant role permissions (tenant level)
export const DEFAULT_TENANT_ROLE_PERMISSIONS = {
  [TENANT_ROLES.OWNER]: [
    // Full tenant access
    PERMISSIONS.NODES_READ,
    PERMISSIONS.NODES_WRITE,
    PERMISSIONS.NODES_DELETE,
    PERMISSIONS.CONSENSUS_READ,
    PERMISSIONS.CONSENSUS_WRITE,
    PERMISSIONS.NETWORK_READ,
    PERMISSIONS.NETWORK_WRITE,
    PERMISSIONS.CRDT_READ,
    PERMISSIONS.CRDT_WRITE,
    PERMISSIONS.SECURITY_READ,
    PERMISSIONS.SECURITY_WRITE,
    PERMISSIONS.SECURITY_ADMIN,
    PERMISSIONS.MONITORING_READ,
    PERMISSIONS.MONITORING_WRITE,
    PERMISSIONS.STORAGE_READ,
    PERMISSIONS.STORAGE_WRITE,
    PERMISSIONS.STORAGE_ADMIN,
    PERMISSIONS.ALERTS_READ,
    PERMISSIONS.ALERTS_WRITE,
    PERMISSIONS.ALERTS_MANAGE,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.CONFIG_WRITE,
    PERMISSIONS.CONFIG_ADMIN,
    PERMISSIONS.AI_READ,
    PERMISSIONS.AI_WRITE,
    PERMISSIONS.AI_ADMIN,
    // Tenant management
    PERMISSIONS.TENANT_READ,
    PERMISSIONS.TENANT_WRITE,
    PERMISSIONS.TENANT_ADMIN,
    PERMISSIONS.TENANT_BILLING,
    PERMISSIONS.TENANT_SETTINGS,
    // User/role management within tenant
    PERMISSIONS.TENANT_USERS_READ,
    PERMISSIONS.TENANT_USERS_WRITE,
    PERMISSIONS.TENANT_USERS_INVITE,
    PERMISSIONS.TENANT_USERS_DELETE,
    PERMISSIONS.TENANT_ROLES_READ,
    PERMISSIONS.TENANT_ROLES_WRITE,
    PERMISSIONS.TENANT_ROLES_DELETE,
  ],
  [TENANT_ROLES.ADMIN]: [
    // Most access except billing and some tenant settings
    PERMISSIONS.NODES_READ,
    PERMISSIONS.NODES_WRITE,
    PERMISSIONS.NODES_DELETE,
    PERMISSIONS.CONSENSUS_READ,
    PERMISSIONS.CONSENSUS_WRITE,
    PERMISSIONS.NETWORK_READ,
    PERMISSIONS.NETWORK_WRITE,
    PERMISSIONS.CRDT_READ,
    PERMISSIONS.CRDT_WRITE,
    PERMISSIONS.SECURITY_READ,
    PERMISSIONS.SECURITY_WRITE,
    PERMISSIONS.MONITORING_READ,
    PERMISSIONS.MONITORING_WRITE,
    PERMISSIONS.STORAGE_READ,
    PERMISSIONS.STORAGE_WRITE,
    PERMISSIONS.ALERTS_READ,
    PERMISSIONS.ALERTS_WRITE,
    PERMISSIONS.ALERTS_MANAGE,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.CONFIG_WRITE,
    PERMISSIONS.AI_READ,
    PERMISSIONS.AI_WRITE,
    // User/role management within tenant
    PERMISSIONS.TENANT_USERS_READ,
    PERMISSIONS.TENANT_USERS_WRITE,
    PERMISSIONS.TENANT_USERS_INVITE,
    PERMISSIONS.TENANT_ROLES_READ,
    PERMISSIONS.TENANT_ROLES_WRITE,
    PERMISSIONS.TENANT_READ,
  ],
  [TENANT_ROLES.OPERATOR]: [
    // Operations focused
    PERMISSIONS.NODES_READ,
    PERMISSIONS.NODES_WRITE,
    PERMISSIONS.CONSENSUS_READ,
    PERMISSIONS.NETWORK_READ,
    PERMISSIONS.CRDT_READ,
    PERMISSIONS.CRDT_WRITE,
    PERMISSIONS.SECURITY_READ,
    PERMISSIONS.MONITORING_READ,
    PERMISSIONS.MONITORING_WRITE,
    PERMISSIONS.STORAGE_READ,
    PERMISSIONS.ALERTS_READ,
    PERMISSIONS.ALERTS_WRITE,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.AI_READ,
    PERMISSIONS.TENANT_READ,
  ],
  [TENANT_ROLES.VIEWER]: [
    // Read-only access
    PERMISSIONS.NODES_READ,
    PERMISSIONS.CONSENSUS_READ,
    PERMISSIONS.NETWORK_READ,
    PERMISSIONS.CRDT_READ,
    PERMISSIONS.SECURITY_READ,
    PERMISSIONS.MONITORING_READ,
    PERMISSIONS.STORAGE_READ,
    PERMISSIONS.ALERTS_READ,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.AI_READ,
    PERMISSIONS.TENANT_READ,
  ],
  [TENANT_ROLES.MEMBER]: [
    // Basic access
    PERMISSIONS.NODES_READ,
    PERMISSIONS.MONITORING_READ,
    PERMISSIONS.ALERTS_READ,
    PERMISSIONS.TENANT_READ,
  ],
} as const;

export type SystemRole = ComplianceServiceTypes.components['schemas']['SystemRole'];
export type PermissionKey = typeof PERMISSIONS[keyof typeof PERMISSIONS];

export type PlatformUser = PlatformAdminTypes.components['schemas']['PlatformUser'];